import axios from 'axios';

import { config } from '../config';
import { getImpersonateUsuarioId } from '../utils/impersonation';
import { getFreshToken } from '../utils/tokenManager';

/**
 * Cliente HTTP autenticado: cada request leva o token do Clerk (renovado pelo
 * tokenManager) e os cabeçalhos de `extras()` — calculados na hora do envio.
 */
function criarCliente(baseURL, extras = () => ({})) {
  // Timeout: uma requisição perdida (ex.: preflight CORS que nunca volta) prendia o
  // loader inicial pra sempre. GET que estoura ou cai na rede tenta mais uma vez.
  const cliente = axios.create({ baseURL, headers: { 'Content-Type': 'application/json' } });
  cliente.interceptors.request.use(async (requisicao) => {
    const token = await getFreshToken();
    if (token) requisicao.headers.Authorization = `Bearer ${token}`;
    Object.assign(requisicao.headers, extras());
    // Só GET de dados: upload e download de arquivo em conexão lenta podem levar mais que isso.
    if (requisicao.method === 'get' && requisicao.responseType !== 'blob' && !requisicao.timeout) {
      requisicao.timeout = 20_000;
    }
    return requisicao;
  });
  cliente.interceptors.response.use(undefined, (erro) => {
    const req = erro.config;
    const semResposta = !erro.response && (erro.code === 'ECONNABORTED' || erro.code === 'ERR_NETWORK');
    if (req && semResposta && req.method === 'get' && !req.__retentativa) {
      req.__retentativa = true;
      return cliente(req);
    }
    return Promise.reject(erro);
  });
  return cliente;
}

/**
 * konq-admin-api (control plane): usuário, branding, tenants e capabilities.
 * Rejeita com o corpo da resposta (CommandResult) quando há um — ver utils/apiError.
 */
export const apiAdmin = criarCliente(config.konqAdminApiUrl);
apiAdmin.interceptors.response.use(
  (resposta) => resposta,
  (erro) => Promise.reject(erro.response?.data ?? erro),
);

/**
 * konq-api (domain plane): tudo do concurso. Vai com o tenant (X-Tenant-Slug) e, quando
 * o Master está acessando como outro usuário, com o id dele — o backend só aceita isso
 * se quem chama for Master.
 */
export const apiConcurso = criarCliente(config.konqConcursoApiUrl, () => {
  const cabecalhos = {};
  const slug = localStorage.getItem('slug');
  if (slug) cabecalhos['X-Tenant-Slug'] = slug;
  const impersonado = getImpersonateUsuarioId();
  if (impersonado) cabecalhos['X-Impersonate-UsuarioId'] = String(impersonado);
  return cabecalhos;
});
apiConcurso.interceptors.response.use(
  (resposta) => {
    // CommandResult com HTTP 200 e sucesso=false (erro de regra no corpo): rejeita no
    // formato de erro do axios pra cair no catch das telas como qualquer outro erro.
    const corpo = resposta.data;
    if (corpo && typeof corpo === 'object' && corpo.sucesso === false) {
      return Promise.reject({ response: { status: corpo.statusCode ?? corpo.code ?? 400, data: corpo } });
    }
    return resposta;
  },
  // Mantém o erro do axios inteiro (response.status e response.data) — ver utils/apiError.
  (erro) => Promise.reject(erro),
);
